import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Download, Link as LinkIcon, Lock } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { NbButton } from "@/components/nb";

// Wall of all 4 streak-milestone badges (locked + unlocked). Screenshottable.
// Props: streak (object with unlocked_ids/current/longest), userName (string), userId (string)
export default function MilestoneWall({ streak, userName = "Student", userId, shareable = true }) {
  const [tiers, setTiers] = useState([]);
  const wallRef = useRef(null);
  const unlockedIds = new Set(streak?.unlocked_ids || streak?.unlocked?.map((m) => m.id) || []);
  const currentStreak = streak?.current || 0;
  const longest = streak?.longest || 0;

  useEffect(() => {
    api.get("/streak/milestones").then((r) => setTiers(r.data)).catch(() => setTiers([]));
  }, []);

  const downloadPng = async () => {
    if (!wallRef.current) return;
    try {
      // Wait a beat so motion animations aren't mid-frame
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(wallRef.current, {
        backgroundColor: "#FDFBF7",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `${(userName || "student").replace(/\W+/g, "_")}_streak_wall.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Saved your wall!");
    } catch (e) {
      toast.error("Couldn't save the PNG");
    }
  };

  const copyLink = async () => {
    if (!userId) return toast.error("No profile id yet");
    const url = `${window.location.origin}/u/${userId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mt-4" data-testid="milestone-wall">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Flame className="w-5 h-5 text-orange-500 fill-orange-300" />
        <h3 className="font-heading font-black text-2xl tracking-tight">Milestone Wall</h3>
        {shareable && (
          <div className="ml-auto flex gap-2">
            <button onClick={downloadPng} data-testid="wall-download" className="inline-flex items-center gap-1 px-3 py-1 rounded-lg nb-border nb-shadow nb-hover bg-white font-mono-sl text-[10px] uppercase">
              <Download className="w-3 h-3" /> Save PNG
            </button>
            <button onClick={copyLink} data-testid="wall-copy-link" className="inline-flex items-center gap-1 px-3 py-1 rounded-lg nb-border nb-shadow nb-hover bg-black text-white font-mono-sl text-[10px] uppercase">
              <LinkIcon className="w-3 h-3" /> Share link
            </button>
          </div>
        )}
      </div>

      <div ref={wallRef} className="rounded-2xl nb-border nb-shadow p-5 bg-white" data-testid="wall-canvas">
        {/* header inside the canvas so it appears in the exported PNG */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-sl text-[10px] uppercase text-neutral-500">EduMarket · Streak Wall</div>
            <div className="font-heading font-black text-xl tracking-tighter">{userName}</div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end">
              <motion.span animate={{ scale: [1, 1.2, 1], rotate: [0, -6, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
                <Flame className="w-6 h-6 text-orange-500 fill-orange-300" />
              </motion.span>
              <span className="font-heading font-black text-3xl">{currentStreak}</span>
            </div>
            <div className="font-mono-sl text-[10px] uppercase text-neutral-500">current · longest {longest}d</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wall-grid">
          {tiers.map((m) => {
            const unlocked = unlockedIds.has(m.id);
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={unlocked ? { rotate: [-2, 2, 0] } : undefined}
                transition={{ duration: 0.4 }}
                className={`rounded-xl nb-border p-4 text-center relative overflow-hidden ${unlocked ? `bg-sl-${m.color} nb-shadow` : "bg-neutral-100"}`}
                data-testid={`wall-tier-${m.id}`}
              >
                {!unlocked && (
                  <div className="absolute top-2 right-2 opacity-70"><Lock className="w-4 h-4" /></div>
                )}
                <div className={`text-5xl ${unlocked ? "" : "grayscale opacity-30"}`}>{m.emoji}</div>
                <div className={`mt-1 font-heading font-black text-lg tracking-tight ${unlocked ? "" : "text-neutral-500"}`}>{m.name}</div>
                <div className="font-mono-sl text-[10px] uppercase mt-0.5">{m.days}-day streak</div>
                {!unlocked && (
                  <div className="mt-2 font-mono-sl text-[10px] uppercase text-neutral-600">
                    {currentStreak >= m.days ? "unlock pending" : `${m.days - currentStreak} to go`}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        <div className="mt-4 font-mono-sl text-[10px] uppercase text-neutral-500 text-center">
          edumarket · earn all four by studying every day
        </div>
      </div>
    </div>
  );
}
